import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { DialogSmartlockDeleteFutureCodesComponent } from '@app/modules/apps/components/dialog-smartlock-delete-future-codes/dialog-smartlock-delete-future-codes.component';
import { NotificationType } from '@app/shared/interfaces';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { MatchedLock, SmartlocksService, UnmatchedLock } from '@app/shared/services/smartlocks/smartlocks.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { IntegrationType, resolveIntegrationInfo } from '@app/shared/utils';

@Component({
  standalone: false,
  selector: 'sbnb-dialog-edit-smartlock',
  templateUrl: './dialog-edit-smartlock.component.html',
  styleUrls: ['./dialog-edit-smartlock.component.scss'],
})
export class DialogEditSmartlockComponent implements OnInit {
  saving: boolean;

  integration_id: string;
  connection_id: string;

  lock: any;
  locks: any[] = [];

  lockIndex = 0;

  integration: IntegrationType | null = null;

  constructor(
    public dialogRef: MatDialogRef<DialogEditSmartlockComponent>,
    private smartlocksService: SmartlocksService,
    private toast: ToastNotificationsService,
    private dialog: MatDialog,
    private segmentIoService: SegmentIoService,
    private readonly router: Router,

    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      integration_id?: string;
      connection_id?: string;
      lock: MatchedLock | UnmatchedLock;
      locks: any[];
    }
  ) { }

  async ngOnInit() {
    this.saving = false;

    // These will only be available if we're adding a new lock - and we've come from the success screen
    this.integration_id = this.dialogData.integration_id;
    this.connection_id = this.dialogData.connection_id;
    this.locks = this.dialogData.locks;

    // This is always provided. It's either just 1 lock (from edit), or the first of many (from success)
    this.lock = this.dialogData.lock;

    if (this.locks.length > 0) {
      this.lock.properties = [];
      this.getLinkedPropertiesList();
    }

    this.resolveIntegrationInfo();

    await this.getAdditionalLockInfo();

    this.setDefaultCodeLength();
  }

  async getAdditionalLockInfo() {
    const payload = {
      integration_id: this.lock.integration_id ? this.lock.integration_id : this.integration_id,
      connection_id: this.lock.connection_id ? this.lock.connection_id : this.connection_id,
      lock_id: this.lock.lock_id ? this.lock.lock_id : this.lock.id,
    };

    this.lock.additional_info = await this.smartlocksService.getAdditionalLockInfo(payload).toPromise();
  }

  getLinkedPropertiesList() {
    const payload = {
      integration_id: this.lock.integration_id ? this.lock.integration_id : this.integration_id,
      connection_id: this.lock.connection_id ? this.lock.connection_id : this.connection_id,
      lock_id: this.lock.lock_id ? this.lock.lock_id : this.lock.id,
    };

    this.smartlocksService.getlinkedPropertiesList(payload).subscribe((res) => {
      this.lock.properties = res;
    });
  }

  resolveIntegrationInfo() {
    const integrationData = resolveIntegrationInfo(this.router.url);

    if (integrationData) {
      this.integration_id = integrationData.integration_id;
      this.integration = integrationData.integration as IntegrationType;
    } else {
      this.toast.open(`Integration is not supported.`, 'Dismiss', NotificationType.Error);
    }
  }

  setDefaultCodeLength() {
    if (this.lock.code_length) {
      return;
    }

    this.lock.code_length = Math.max(
      this.lock.additional_info.min_length_pincode,
      this.lock.additional_info.max_length_pincode
    );
  }

  storeSmartlock() {
    this.saving = true;

    if (this.lock.properties.length < 1) {
      this.loadNewLock();
      return;
    }

    const payload = {
      integration_id: this.integration_id,
      connection_id: this.connection_id,
      lock_id: this.lock.id,
      code_length: this.lock.code_length,
      properties: this.lock.properties,
      additional_info: this.lock.additional_info,
    };

    this.smartlocksService.storeSmartlock(payload).subscribe((res) => {
      if (res.errors) {
        this.toast.open(Object.values(res.errors)[0] as string, 'Dismiss', NotificationType.Error);
        return;
      }
    });

    this.loadNewLock();
  }

  storeUnlinkedSmartlock() {
    this.saving = true;

    const payload = {
      integration_id: this.lock.integration_id,
      connection_id: this.lock.connection_id,
      lock_id: this.lock.id,
      code_length: this.lock.code_length,
      properties: this.lock.properties,
      additional_info: this.lock.additional_info,
      force_resync: true,
    };

    this.smartlocksService.storeSmartlock(payload).subscribe((res) => {
      if (res.errors) {
        this.toast.open(Object.values(res.errors)[0] as string, 'Dismiss', NotificationType.Error);
        return;
      }
    });

    this.dialogRef.close();
  }

  avoidMatch() {
    if (this.lock.is_supported) {
      this.dialog.open(DialogSmartlockDeleteFutureCodesComponent, {
        width: '720px',
        maxHeight: '90vh',
        data: {
          lock: this.lock,
        },
      });
    }

    this.segmentIoService.track(SegmentEvent.SmartLockNotMatchedSkipped, {
      smartlock: {
        name: this.lock.additional_info.display_name,
        lock_id: this.lock.id,
        integration_id: this.lock.integration_id,
      },
    });

    this.loadNewLock();
  }

  setLockProperties(properties: { all_properties: boolean; properties: number[] }) {
    this.lock.properties = properties?.properties;
  }

  newPropertyChecked(property) {
    this.saving = true;
    const payload = {
      property_id: property.property,
      code_length: this.lock.code_length,
      lock_id: this.lock.lock_id ? this.lock.lock_id : this.lock.id,
    };

    this.smartlocksService.validateCodeLengthAgainstProperty(payload).subscribe((res) => {
      if (res?.errors) {
        this.toast.open(res.message, 'Dismiss', NotificationType.Error);
        this.lock.properties = this.lock.properties.filter((num) => num !== property.property);
        this.saving = false;
        return;
      }
      this.saving = false;
    });
  }

  editSmartlock() {
    if (!this.lock.is_supported) {
      return;
    }
    this.saving = true;
    const payload = {
      lock: this.lock,
    };

    this.smartlocksService.editSmartlock(payload).subscribe((res) => {
      if (res.errors) {
        this.toast.open(Object.values(res.errors)[0] as string, 'Dismiss', NotificationType.Error);
        return;
      }
      this.dialogRef.close();
    });
  }

  loadNewLock() {
    ++this.lockIndex;
    const codeLength = this.lock.code_length;
    if (this.locks.length >= this.lockIndex + 1) {
      this.lock = [];
      this.lock.properties = [];
      this.getLinkedPropertiesList();
      this.lock.code_length = codeLength;
      this.lock.additional_info = this.locks[this.lockIndex];
      this.lock.id = this.locks[this.lockIndex].id;
      this.saving = false;
    } else {
      this.dialogRef.close();
    }
  }

  validateCodeLengthInput() {
    if (this.lock.code_length < this.lock.additional_info.min_length_pincode) {
      this.lock.code_length = this.lock.additional_info.min_length_pincode;
    }

    if (this.lock.code_length > this.lock.additional_info.max_length_pincode) {
      this.lock.code_length = this.lock.additional_info.max_length_pincode;
    }
  }
}
